// Channel list: GET /sites/{id}/logs; tail: GET /sites/{id}/logs/{channel}.
// Tail payload is the RESHAPED gateway response (raw lines[].line + channel metadata).
import { useState } from 'react';
import { useSiteLogs, useSiteLogTail, useToggleLogChannel } from '../hooks';
import { QueryState } from '../../../components/QueryState';
import { formatInt } from '../../../lib/format';
import type { LogChannelsResponse } from '../../../lib/types';

const ID = 'em-mgr--site--logs';

export interface LogsTabProps {
  siteId: string;
  /** Selected channel from the URL. Null = nothing selected yet. */
  channel?: string | null;
  /** Navigate to another channel. Omitted → local fallback state (unit tests). */
  onChannelChange?: (channel: string | null) => void;
}

export function LogsTab({ siteId, channel: routeChannel = null, onChannelChange }: LogsTabProps) {
  const { data, isPending, isError, error } = useSiteLogs(siteId);
  // Channel lives in the URL so a tail can be linked/refreshed; falls back to
  // local state when no onChannelChange is supplied (unit tests).
  const [localChannel, setLocalChannel] = useState<string | null>(routeChannel);
  const channel = onChannelChange ? routeChannel : localChannel;
  const setChannel = onChannelChange ?? setLocalChannel;
  const tail = useSiteLogTail(siteId, channel);
  const toggle = useToggleLogChannel(siteId);

  return (
    <QueryState<LogChannelsResponse>
      idBase={ID}
      isPending={isPending}
      isError={isError}
      error={error}
      data={data}
    >
      {(d) =>
        d.channels.length === 0 ? (
          <div
            data-id={`${ID}--empty`}
            className="rounded-lg border border-dashed border-slate-300 p-8 text-center text-slate-500 dark:border-slate-700"
          >
            No log channels registered for this site.
          </div>
        ) : (
          <div data-id={ID} className="grid gap-4 sm:grid-cols-[220px_1fr]">
            <ul className="space-y-1">
              {d.channels.map((c) => (
                <li
                  key={c.slug}
                  data-id={`${ID}--channel`}
                  className={`flex items-center justify-between rounded-md pr-2 text-sm ${
                    channel === c.slug
                      ? 'bg-slate-200 dark:bg-slate-800'
                      : 'hover:bg-slate-100 dark:hover:bg-slate-800/60'
                  }`}
                >
                  <button
                    type="button"
                    data-id={`${ID}--channel-select`}
                    onClick={() => setChannel(c.slug)}
                    className="flex-1 px-2 py-1 text-left"
                  >
                    <span className="font-medium">{c.slug}</span>
                    {/* Size and enabled state are independent; hiding size at 0B avoids a freshly-enabled channel reading as disabled. */}
                  </button>
                  <span className="flex items-center gap-1.5">
                    {c.sizeBytes > 0 && (
                      <span
                        data-id={`${ID}--channel-size`}
                        className="text-slate-400 dark:text-slate-500"
                      >
                        {formatInt(c.sizeBytes)}B
                      </span>
                    )}
                    {/* Toggle sits beside (not inside) the select button — nested buttons are invalid HTML and must not also switch the tailed channel. */}
                    <button
                      type="button"
                      role="switch"
                      aria-checked={c.enabled}
                      aria-label={`${c.enabled ? 'Disable' : 'Enable'} ${c.slug}`}
                      data-id={`${ID}--channel-toggle`}
                      disabled={toggle.isPending}
                      onClick={() => toggle.mutate({ channel: c.slug, on: !c.enabled })}
                      className={`rounded px-1 disabled:opacity-50 ${
                        c.enabled
                          ? 'text-green-600 hover:bg-green-100 dark:text-green-400 dark:hover:bg-green-900/40'
                          : 'text-slate-400 hover:bg-slate-200 dark:text-slate-500 dark:hover:bg-slate-700'
                      }`}
                    >
                      {c.enabled ? 'on' : 'off'}
                    </button>
                  </span>
                </li>
              ))}
            </ul>

            <div className="rounded-lg border border-slate-200 p-2 dark:border-slate-800">
              {channel === null ? (
                <p className="p-2 text-sm text-slate-500 dark:text-slate-400">
                  Select a channel to tail its log.
                </p>
              ) : tail.isPending ? (
                <p data-id={`${ID}--tail-loading`} className="p-2 text-sm text-slate-500 dark:text-slate-400">
                  Loading…
                </p>
              ) : tail.isError ? (
                <p data-id={`${ID}--tail-error`} className="p-2 text-sm text-red-600 dark:text-red-400">
                  {tail.error instanceof Error ? tail.error.message : 'Failed'}
                </p>
              ) : (
                <>
                  <div
                    data-id={`${ID}--tail-meta`}
                    className="flex flex-wrap items-center gap-3 border-b border-slate-200 px-2 pb-2 text-xs text-slate-500 dark:border-slate-800 dark:text-slate-400"
                  >
                    <span data-id={`${ID}--tail-meta--label`} className="font-medium text-slate-700 dark:text-slate-200">
                      {tail.data.label}
                    </span>
                    <span
                      data-id={`${ID}--tail-meta--enabled`}
                      className={
                        tail.data.enabled
                          ? 'text-green-600 dark:text-green-400'
                          : 'text-slate-400 dark:text-slate-500'
                      }
                    >
                      {tail.data.enabled ? 'enabled' : 'disabled'}
                    </span>
                    <span data-id={`${ID}--tail-meta--total-lines`}>
                      {formatInt(tail.data.totalLines)} lines
                    </span>
                    <span data-id={`${ID}--tail-meta--size`}>{formatInt(tail.data.size)}B</span>
                  </div>
                  {tail.data.lines.length === 0 ? (
                    <p
                      data-id={`${ID}--tail-empty`}
                      className="p-2 text-sm text-slate-500 dark:text-slate-400"
                    >
                      {tail.data.exists ? 'Log file is empty.' : 'No log file on disk yet.'}
                    </p>
                  ) : (
                    <pre
                      data-id={`${ID}--tail`}
                      className="max-h-96 overflow-auto whitespace-pre-wrap p-2 font-mono text-xs text-slate-700 dark:text-slate-300"
                    >
                      {tail.data.lines.map((l, i) => (
                        <div key={i} data-id={`${ID}--tail-line`}>
                          {l.line}
                        </div>
                      ))}
                    </pre>
                  )}
                </>
              )}
            </div>
          </div>
        )
      }
    </QueryState>
  );
}
